import { useContext, useMemo, useRef, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { useGlobalEffectActions, usePageContext } from "@ryde/redux";
import {
  liveMapRideFilterAtomFamily,
  LiveMapRideFilterEnum,
  liveMapRideFilterTranslationKey,
} from "@ryde/shared";

import { LiveMapContext } from "../../../../context";

export type TUseLiveMapRideFilterDropdown = ReturnType<
  typeof useLiveMapRideFilterDropdown
>;

export const useLiveMapRideFilterDropdown = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const { fetchRidesWithLoader } = useContext(LiveMapContext);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [, startTransition] = useTransition();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [effectiveRideFilter, setEffectiveRideFilter] = useRecoilState(
    liveMapRideFilterAtomFamily(entity),
  );

  const tooltipContent = useMemo(
    () => t(liveMapRideFilterTranslationKey(effectiveRideFilter)),
    [t, effectiveRideFilter],
  );

  const togglePopover = useEvent((): void => {
    setIsPopoverOpen(prevOpen => !prevOpen);
  });

  const closePopover = useEvent((event: MouseEvent | TouchEvent): void => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }

    setIsPopoverOpen(false);
  });

  const handleItemClick = useEvent(
    (
      _event: React.MouseEvent | React.TouchEvent,
      value: LiveMapRideFilterEnum,
    ): void => {
      setIsPopoverOpen(false);

      if (value === effectiveRideFilter) {
        return;
      }

      startTransition(() => {
        setEffectiveRideFilter(value);
        resetPaginationAppEvent();
        fetchRidesWithLoader?.();
      });
    },
  );

  return {
    anchorRef,
    closePopover,
    togglePopover,
    isPopoverOpen,
    effectiveRideFilter,
    handleItemClick,
    tooltipContent,
  };
};
